<template>
    <section
        aria-labelledby="xmoReleaseCalendar"
        class="padding-bottom-50"
        :class="[
            isNewsAvailable ? '' : 'padding-top-70',
            canShowMarketFilter ? 'padding-bottom-s-0' : 'padding-bottom-s-30',
        ]">
        <div
            v-if="ajaxError"
            class="alert alert-error animated">
            {{ ajaxError }}
        </div>
        <div v-else>
            <ReleaseFilter
                v-if="canShowMarketFilter"
                :is-mobile="isMobile"
                :market-name="getMarketName"
                :locale="locale"
                @filter-releases="setFilter($event)" />
            <template v-else>
                <div
                    v-once
                    class="text-center text-s-left">
                    <h2 class="sectionRooftitle">
                        {{ trans('RELEASE_CALENDAR') }}
                    </h2>
                    <h3 class="h2 margin-bottom-15 text-center">
                        {{ trans('INFOGRAPHIC_RELEASE_CALENDAR_PLANNED') }}
                    </h3>
                </div>
            </template>
            <div
                v-if="!isMobile"
                class="margin-horizontal-80 margin-horizontal-s-0 margin-top-50">
                <div class="flex pos-relative">
                    <div
                        v-if="upcomingReleasesListLength > 3"
                        class="releaseSlider">
                        <button
                            type="button"
                            class="glide__arrow glide__arrow--left glide__release--left center"
                            :class="showPreviousButton ? '' : 'opacity-30 pointerEvent--none'"
                            :aria-label="trans('GLOBAL_PREVIOUS_MONTH')"
                            :aria-disabled="!showPreviousButton"
                            data-gtm="upcomingReleasesButton--previous"
                            @click="previous">
                            <svg aria-hidden="true">
                                <use href="#iconChevronLeftBold" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            class="glide__arrow glide__arrow--right glide__release--right center"
                            :class="showNextButton ? '' : 'opacity-30 pointerEvent--none'"
                            :aria-label="trans('GLOBAL_NEXT_MONTH')"
                            :aria-disabled="!showNextButton"
                            data-gtm="upcomingReleasesButton--next"
                            @click="next">
                            <svg aria-hidden="true">
                                <use href="#iconChevronRightBold" />
                            </svg>
                        </button>
                    </div>
                    <template v-for="(row, monthName, index) in getDisplayedMonths(getUpcomingReleases())">
                        <div
                            :key="index"
                            class="flex__item flex--directionColumn padding-horizontal-10 flex--grow1 width33 padding-horizontal-s-0">
                            <release-list-title
                                :month-name="monthName"
                                :upcoming-releases="getUpcomingReleases()"
                                :show-monthly="showMonthly" />
                            <div class="padding-right-10 scrollBox max-height-300">
                                <release-list
                                    :release-list-data="getReleasesData"
                                    :filtered-categories="filteredCategories"
                                    :time-frame="row"
                                    :initialized="initialized"
                                    :no-data-info="trans('SMI_RELEASE_NO_UPDATES')"
                                    :panel-delay="index * 200"
                                    :filter-by-content-type="filterByContentType"
                                    @open-release-modal="openReleaseModal" />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div
                v-else
                class="scrollBox max-height-300 padding-right-10">
                <template v-for="(row, monthName, index) in getDisplayedMonths(getUpcomingReleases())">
                    <div :key="index">
                        <release-list-title
                            :month-name="monthName"
                            :upcoming-releases="getUpcomingReleases()"
                            :show-monthly="showMonthly" />
                        <release-list
                            :release-list-data="getReleasesData"
                            :filtered-categories="filteredCategories"
                            :time-frame="row"
                            :initialized="initialized"
                            :no-data-info="trans('SMI_RELEASE_NO_UPDATES')"
                            :panel-delay="index * 200"
                            :filter-by-content-type="filterByContentType"
                            @open-release-modal="openReleaseModal" />
                    </div>
                </template>
            </div>
            <div class="text-center float-none width100 margin-top-10 margin-bottom-30">
                <a
                    class="button button--medium button--primary"
                    data-gtm="xmoHubpage__upcomingReleases"
                    :class="{ 'button--fullwidth': isMobile }"
                    :href="releasesPath">
                    {{ trans('OUTLOOK_UPCOMING_RELEASES') }}
                </a>
            </div>
        </div>
        <modal
            :modal-open="releaseModalOpen"
            :custom-container-classes="['vueModal__container--releaseCalendar']"
            @toggle-modal="releaseModalOpen = false">
            <div
                v-if="renderModalContent"
                class="text-color--dark-blue-gray">
                <div v-if="releaseModalContent.releaseDate">
                    {{ getLocalizedDate(releaseModalContent.releaseDate.date) }}
                </div>
                <h3 class="margin-bottom-10">
                    {{ releaseModalContent.title }}
                </h3>
                <div
                    class="margin-bottom-5"
                    v-html="releaseModalContent.description">
                </div>
                <div
                    v-if="releaseModalContent.link"
                    class="margin-top-10 margin-bottom-5">
                    <a
                        :href="releaseModalContent.link"
                        class="linkArrowHover">
                        <svg
                            class="stat__svgIcon"
                            aria-hidden="true">
                            <use href="#iconArrowRightLongBold" />
                        </svg>
                        {{ trans('SMI_RELEASE_BUTTON_TEXT') }}
                    </a>
                </div>
            </div>
        </modal>
    </section>
</template>

<script>
    import axios from 'axios';
    import Translator from 'tools/trans';
    import modal from 'components/modal';
    import ReleaseList from 'releases/components/releaseList';
    import ReleaseListTitle from 'releases/components/releaseListTitle';
    import { isMobile } from 'tools/isMobile';
    import { getPath } from 'tools/getFromHtml';
    import { mapState } from 'vuex';
    import { generateMarketName } from 'outlook/utils/generateMarketName';
    import { outlookAbbreviationsMap } from 'outlook/constants/constants';

    const CURRENT_DATE = new Date();

    export default {
        components: {
            ReleaseList,
            ReleaseListTitle,
            'modal': modal,
            ReleaseFilter: () => import('outlook/components/marketHighlights/releaseFilter'),
        },
        props: {
            filterByContentType: {
                type: Boolean,
                default: true,
            },
            showMonthlyInit: {
                type: Boolean,
                default: false,
            },
            isNewsAvailable: {
                type: Boolean,
                required: true,
            },
            canShowMarketFilter: {
                type: Boolean,
                default: false,
            },
            locale: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                ajaxError: null,
                releaseListData: null,
                getReleaseListPath: getPath('outlookReleases'),
                initialized: false,
                isMobile: isMobile(),
                releaseModalOpen: false,
                releaseModalContent: {},
                currentYear: CURRENT_DATE.getFullYear(),
                currentMonth: CURRENT_DATE.getMonth(),
                currentDay: CURRENT_DATE.getDate(),
                currentDayOfWeek: (CURRENT_DATE.getDay() === 0) ? 7 : CURRENT_DATE.getDay(),
                firstDisplayedMonth: 0,
                displayedTimeFrames: { monthly: 6, weekly: 12 },
                displayedHistoryLength: 30,
                showMonthly: true,
                renderModalContent: false,
                filterReleases: false,
                abbreviationsMap: outlookAbbreviationsMap(),
            };
        },
        computed: {
            ...mapState([
                'currentOutlook',
            ]),
            upcomingReleasesListLength() {
                return Object.keys(this.getUpcomingReleases()).length;
            },
            showPreviousButton() {
                return this.firstDisplayedMonth >= 1;
            },
            showNextButton() {
                return this.firstDisplayedMonth < this.upcomingReleasesListLength - 3;
            },
            getReleasesFilteredByMarket() {
                if (this.isNotEmptyArray(this.releaseListData) && this.canShowMarketFilter) {
                    return this.releaseListData
                        .filter(release => release.contentType === this.abbreviationsMap
                            .get(this.currentOutlook.toLocaleLowerCase()));
                }

                return [];
            },
            getReleasesData() {
                if (this.filterReleases) {
                    /* Ensure I only filter releases once even on multiple toggles */
                    return this.getReleasesFilteredByMarket;
                }

                return this.releaseListData;
            },
            getMarketName() {
                return this.generateMarketName(this.currentOutlook.toLocaleLowerCase());
            },
            releasesPath() {
                if (!this.filterReleases) {
                    return getPath('releases');
                }

                return `${ getPath('releases') }#${ this.currentOutlook.toLocaleLowerCase() }`;
            },
            filteredCategories() {
                return this.filterReleases ? [this.abbreviationsMap.get(this.currentOutlook.toLowerCase())] :
                    [...this.abbreviationsMap.values()];
            },
        },
        methods: {
            trans: Translator.trans,
            generateMarketName,
            getData() {
                axios.get(this.getReleaseListPath).then(response => {
                    const data = response.data;
                    if (typeof data === 'object') {
                        this.releaseListData = data;
                    } else {
                        console.error(response);
                        this.ajaxError = Translator.trans('GLOBAL_ERROR_OCCURED');
                    }
                }).catch(response => {
                    console.error(response);
                    this.ajaxError = Translator.trans('GLOBAL_ERROR_OCCURED');
                }).finally(() => {
                    this.initialized = true;
                });
            },
            isNotEmptyArray(value) {
                return Array.isArray(value) && value.length > 1;
            },
            setFilter($event) {
                this.filterReleases = $event;
            },
            getDisplayedMonths(upcomingReleases) {
                const months = {};
                for (let i = this.firstDisplayedMonth; i < this.firstDisplayedMonth + 3; i++) {
                    const key = Object.keys(upcomingReleases)[i];
                    months[key] = upcomingReleases[key];
                }

                return months;
            },
            getUpcomingReleases() {
                return this.getUpcomingTimeFrames(this.showMonthly ? this.displayedTimeFrames.monthly : this.displayedTimeFrames.weekly);
            },
            getUpcomingTimeFrames(timeFrames) {
                const timeFrameObject = {};
                for (let i = 1; i <= timeFrames; i++) {
                    if (this.showMonthly) {
                        if (i === 1) {
                            timeFrameObject[`next${ i }`] = [
                                this.getTimestamp(0, this.currentDay),
                                this.getTimestamp(i, 0),
                            ];
                        } else {
                            const weekStart = i - 1;
                            timeFrameObject[`next${ i }`] = [
                                this.getTimestamp(weekStart),
                                this.getTimestamp(i, 0),
                            ];
                        }
                    } else {
                        if (i === 1) {
                            timeFrameObject[`next${ i }`] = [
                                this.getTimestamp(0, this.currentDay),
                                this.getTimestamp(0, this.getDayFromFirstOfCurrentWeek(6)),
                            ];
                        } else {
                            const weekStart = (i - 1) * 7;
                            const weekEnd = (i - 1) * 7 + 6;
                            timeFrameObject[`next${ i }`] = [
                                this.getTimestamp(0, this.getDayFromFirstOfCurrentWeek(weekStart)),
                                this.getTimestamp(0, this.getDayFromFirstOfCurrentWeek(weekEnd)),
                            ];
                        }
                    }
                }

                return timeFrameObject;
            },
            getDayFromFirstOfCurrentWeek(daysFromNow = 0) {
                return this.currentDay - this.currentDayOfWeek + daysFromNow;
            },
            getTimestamp(month = 0, days = 1) {
                return new Date(this.currentYear, this.currentMonth + month, days);
            },
            getLocalizedDate(timestamp) {
                return new Date(timestamp).toLocaleDateString();
            },
            openReleaseModal(releaseData) {
                this.renderModalContent = true;
                this.releaseModalContent = releaseData;
                this.releaseModalOpen = true;
            },
            previous() {
                if (this.showPreviousButton) {
                    this.firstDisplayedMonth = this.firstDisplayedMonth - 1;
                }
            },
            next() {
                if (this.showNextButton) {
                    this.firstDisplayedMonth = this.firstDisplayedMonth + 1;
                }
            },
        },
        created() {
            this.getData();
        },
    };
</script>
